import { supabase } from './supabase';
import { generateSlug } from './slugUtils';

/**
 * Valida los datos del evento antes de crear
 */
export const validateEventData = (eventData) => {
    const errors = [];

    // Validar nombre
    if (!eventData.event_name || eventData.event_name.trim().length < 3) {
        errors.push('El nombre del evento debe tener al menos 3 caracteres');
    }

    // Validar sesiones
    if (!eventData.config?.sessions || eventData.config.sessions.length === 0) {
        errors.push('Debe configurar al menos una sesión');
    }

    // Validar cada sesión
    eventData.config?.sessions?.forEach((session, index) => {
        if (!session.event_name || session.event_name.trim().length === 0) {
            errors.push(`La sesión ${index + 1} debe tener un nombre`);
        }
        if (!session.seatCount || session.seatCount < 1) {
            errors.push(`La sesión ${index + 1} debe tener al menos 1 asiento`);
        }
    });

    // Validar fechas
    if (eventData.start_date && eventData.end_date) {
        const start = new Date(eventData.start_date);
        const end = new Date(eventData.end_date);
        if (start > end) {
            errors.push('La fecha de inicio debe ser anterior o igual a la fecha de fin');
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Crea un evento completo en Supabase
 */
export const createEvent = async (eventData) => {
    try {
        // Validar datos
        const validation = validateEventData(eventData);
        if (!validation.isValid) {
            return {
                success: false,
                errors: validation.errors
            };
        }

        // Generar slug único
        const slug = await generateSlug(eventData.event_name);

        // Las imágenes ya son URLs de Storage (no base64) — guardar todo en un solo insert
        const configLimpio = {
            ...eventData.config,
            sessions: (eventData.config.sessions || []).map(s => {
                // Descartar base64 residual por si acaso (no debería llegar aquí)
                const image = s.image && !s.image.startsWith('data:') ? s.image : (s.image && s.image.startsWith('data:') ? '' : (s.image || ''));
                return { ...s, image };
            })
        };

        const eventToCreate = {
            event_name: eventData.event_name.trim(),
            cycling_room: eventData.cycling_room || '',
            event_slug: slug,
            start_date: eventData.start_date || null,
            end_date: eventData.end_date || null,
            is_active: eventData.is_active !== undefined ? eventData.is_active : true,
            config: configLimpio,
            ...(eventData.cycling_room_logo && !eventData.cycling_room_logo.startsWith('data:') ? { cycling_room_logo: eventData.cycling_room_logo } : {}),
            ...(eventData.event_image && !eventData.event_image.startsWith('data:') ? { event_image: eventData.event_image } : {})
        };

        const { data: createdEvent, error: eventError } = await supabase
            .from('events')
            .insert([eventToCreate])
            .select()
            .single();

        if (eventError) {
            console.error('Error creating event:', eventError);
            return {
                success: false,
                errors: ['Error al crear el evento: ' + eventError.message]
            };
        }

        // Crear asientos para cada sesión
        const seatsCreated = await createSeatsForEvent(createdEvent.id, eventData.config.sessions);

        if (!seatsCreated.success) {
            // Si falla la creación de asientos, eliminar el evento
            await supabase.from('events').delete().eq('id', createdEvent.id);
            return {
                success: false,
                errors: ['Error al crear los asientos: ' + seatsCreated.errors.join(', ')]
            };
        }

        // Crear usuario administrador para el evento
        const adminUsername = eventData.adminUser?.username || eventData.admin_username;
        const adminPassword = eventData.adminUser?.password || eventData.admin_password;
        const adminEmail = eventData.adminUser?.email;

        if (adminUsername && adminPassword) {
            const adminCreated = await createEventAdmin(
                createdEvent.id,
                adminUsername,
                adminPassword,
                eventData.event_name,
                adminEmail
            );

            if (!adminCreated.success) {
                console.warn('Advertencia: No se pudo crear el administrador del evento:', adminCreated.errors);
                // No eliminamos el evento si falla el admin, solo advertimos
            }
        }

        return {
            success: true,
            event: createdEvent,
            seatsCreated: seatsCreated.count
        };

    } catch (error) {
        console.error('Error in createEvent:', error);
        return {
            success: false,
            errors: ['Error inesperado: ' + error.message]
        };
    }
};

/**
 * Crea asientos para todas las sesiones de un evento
 */
export const createSeatsForEvent = async (eventId, sessions) => {
    try {
        const allSeats = [];

        // Para cada sesión, crear los asientos según la configuración de filas
        // Use the layout from the first session (assuming all sessions share the same physical layout)
        const masterSession = sessions[0];

        if (masterSession) {
            const rowConfig = masterSession.rowConfiguration || [6, 5, 5, 5, 6];
            let seatNumber = 1;

            rowConfig.forEach((seatsInRow, rowIndex) => {
                const numericSeats = typeof seatsInRow === 'object' ? seatsInRow.seatCount : seatsInRow;
                const count = parseInt(numericSeats) || 0;

                for (let i = 0; i < count; i++) {
                    allSeats.push({
                        event_id: eventId,
                        seat_number: seatNumber,
                        row_number: rowIndex + 1,
                        is_selectable: true
                    });
                    seatNumber++;
                }
            });
        }

        // Insertar todos los asientos
        const { data, error } = await supabase
            .from('seats')
            .insert(allSeats)
            .select();

        if (error) {
            console.error('Error creating seats:', error);
            return {
                success: false,
                errors: [error.message]
            };
        }

        return {
            success: true,
            count: data.length
        };

    } catch (error) {
        console.error('Error in createSeatsForEvent:', error);
        return {
            success: false,
            errors: [error.message]
        };
    }
};

/**
 * Actualiza el layout de asientos sin borrar reservas.
 * - Asientos existentes: se actualiza su row_number según el nuevo rowConfiguration
 * - Asientos sobrantes (filas eliminadas): se eliminan solo si no tienen reservas
 * - Asientos nuevos: se crean si el total aumentó
 */
export const updateSeatsLayout = async (eventId, sessions) => {
    try {
        const masterSession = sessions[0];
        if (!masterSession) return { success: true };

        const rowConfig = masterSession.rowConfiguration || [6, 5, 5, 5, 6];

        // Construir mapa nuevo: seat_number → row_number
        const newLayout = {};
        let seatNum = 1;
        rowConfig.forEach((count, rowIdx) => {
            const n = parseInt(count) || 0;
            for (let i = 0; i < n; i++) {
                newLayout[seatNum] = rowIdx + 1;
                seatNum++;
            }
        });
        const newTotal = seatNum - 1;

        // Traer asientos actuales ordenados por seat_number
        const { data: existingSeats, error: fetchError } = await supabase
            .from('seats')
            .select('id, seat_number, row_number')
            .eq('event_id', eventId)
            .order('seat_number');

        if (fetchError) return { success: false, errors: [fetchError.message] };

        const existingTotal = existingSeats?.length || 0;

        // 1. Actualizar row_number de asientos que cambian de fila
        const toUpdate = (existingSeats || []).filter(s => {
            const newRow = newLayout[s.seat_number];
            return newRow !== undefined && newRow !== s.row_number;
        });

        for (const seat of toUpdate) {
            await supabase.from('seats')
                .update({ row_number: newLayout[seat.seat_number] })
                .eq('id', seat.id);
        }

        // 2. Eliminar asientos sobrantes (seat_number > newTotal) que no tengan reservas
        if (existingTotal > newTotal) {
            const surplusSeats = (existingSeats || []).filter(s => s.seat_number > newTotal);
            for (const seat of surplusSeats) {
                const { data: res } = await supabase
                    .from('reservations').select('id').eq('seat_id', seat.id).limit(1);
                if (!res || res.length === 0) {
                    await supabase.from('seats').delete().eq('id', seat.id);
                }
                // Si tiene reservas, se deja el asiento aunque ya no esté en el layout
            }
        }

        // 3. Crear asientos nuevos si el total aumentó
        if (newTotal > existingTotal) {
            const newSeats = [];
            for (let n = existingTotal + 1; n <= newTotal; n++) {
                newSeats.push({
                    event_id: eventId,
                    seat_number: n,
                    row_number: newLayout[n],
                    is_selectable: true
                });
            }
            if (newSeats.length > 0) {
                await supabase.from('seats').insert(newSeats);
            }
        }

        return { success: true };
    } catch (error) {
        return { success: false, errors: [error.message] };
    }
};

/**
 * Actualiza un evento existente
 */
export const updateEvent = async (eventId, eventData) => {
    try {
        // Extraer imágenes base64 del config para enviarlas por separado
        // Las imágenes ya son URLs de Storage — un solo update sin base64
        const configLimpio = {
            ...eventData.config,
            sessions: (eventData.config.sessions || []).map(s => {
                const image = s.image && !s.image.startsWith('data:') ? s.image : '';
                return { ...s, image };
            })
        };

        const updatePayload = {
            event_name: eventData.event_name,
            cycling_room: eventData.cycling_room,
            start_date: eventData.start_date,
            end_date: eventData.end_date,
            is_active: eventData.is_active,
            config: configLimpio
        };
        if (eventData.cycling_room_logo !== undefined) {
            updatePayload.cycling_room_logo = eventData.cycling_room_logo && !eventData.cycling_room_logo.startsWith('data:')
                ? eventData.cycling_room_logo : '';
        }
        if (eventData.event_image !== undefined) {
            updatePayload.event_image = eventData.event_image && !eventData.event_image.startsWith('data:')
                ? eventData.event_image : '';
        }

        const { data, error } = await supabase
            .from('events')
            .update(updatePayload)
            .eq('id', eventId)
            .select()
            .single();

        if (error) {
            console.error('Error updating event:', error);
            return {
                success: false,
                errors: ['Error al actualizar el evento: ' + error.message]
            };
        }

        // Actualizar layout de asientos preservando reservas
        const seatsResult = await updateSeatsLayout(eventId, eventData.config.sessions);
        if (!seatsResult.success) {
            console.warn('Advertencia al actualizar layout de asientos:', seatsResult.errors);
        }



        // Actualizar o crear usuario administrador si se proporcionan credenciales
        const adminUsername = eventData.adminUser?.username || eventData.admin_username;
        const adminPassword = eventData.adminUser?.password || eventData.admin_password;
        const adminEmail = eventData.adminUser?.email;

        if (adminUsername && adminPassword) {
            // Verificar si ya existe un admin para este evento
            const { data: existingAdmin } = await supabase
                .from('event_admins')
                .select('*')
                .eq('event_id', eventId)
                .maybeSingle();

            if (existingAdmin) {
                // Actualizar admin existente
                const { error: updateAdminError } = await supabase
                    .from('event_admins')
                    .update({
                        username: adminUsername,
                        password_hash: adminPassword,
                        email: adminEmail || existingAdmin.email,
                        full_name: `Admin - ${eventData.event_name}`
                    })
                    .eq('id', existingAdmin.id);

                if (updateAdminError) {
                    console.warn('Advertencia: No se pudo actualizar el administrador:', updateAdminError);
                } else {
                    console.log('✅ Administrador actualizado exitosamente');
                }
            } else {
                // Crear nuevo admin si no existe
                const adminCreated = await createEventAdmin(
                    eventId,
                    adminUsername,
                    adminPassword,
                    eventData.event_name,
                    adminEmail
                );

                if (!adminCreated.success) {
                    console.warn('Advertencia: No se pudo crear el administrador del evento:', adminCreated.errors);
                }
            }
        }

        return {
            success: true,
            event: data
        };

    } catch (error) {
        return {
            success: false,
            errors: [error.message]
        };
    }
};

/**
 * Elimina un evento y todos sus datos relacionados
 */
export const deleteEvent = async (eventId) => {
    try {
        // Supabase eliminará automáticamente los datos relacionados por CASCADE
        const { error } = await supabase
            .from('events')
            .delete()
            .eq('id', eventId);

        if (error) {
            return {
                success: false,
                errors: [error.message]
            };
        }

        return {
            success: true
        };

    } catch (error) {
        return {
            success: false,
            errors: [error.message]
        };
    }
};

/**
 * Crea un usuario administrador para un evento
 */
export const createEventAdmin = async (eventId, username, password, eventName, email = null) => {
    try {
        const { data, error } = await supabase
            .from('event_admins')
            .insert([{
                event_id: eventId,
                username: username,
                password_hash: password, // En producción, debe hashearse
                email: email || `${username}@event.local`,
                full_name: `Admin - ${eventName}`
            }])
            .select()
            .single();

        if (error) {
            console.error('Error creating event admin:', error);
            return {
                success: false,
                errors: [error.message]
            };
        }

        console.log('✅ Administrador de evento creado exitosamente:', data);
        return {
            success: true,
            admin: data
        };

    } catch (error) {
        console.error('Error in createEventAdmin:', error);
        return {
            success: false,
            errors: [error.message]
        };
    }
};
